import React from "react";
import { AlertCircle, CheckCircle2, Info, Loader2, XCircle } from "lucide-react";
import { businessErrorMessage } from "../../api/businessErrors";

const ICONS = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
};

const STYLES = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400",
  error: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-400",
  warning: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400",
  info: "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-400",
};

export function getApiErrorMessage(error, fallback = "Une erreur est survenue. Veuillez reessayer.") {
  const business = businessErrorMessage(error);
  if (business) return business;

  const status = error?.response?.status;
  const data = error?.response?.data;
  const raw = typeof data === "string"
    ? data
    : data?.detail || data?.message || data?.error || data?.non_field_errors?.[0] || error?.message;

  const normalized = String(raw || "").toLowerCase();
  const code = String(data?.code || "").toLowerCase();

  if (code === "remote_product_access_denied") {
    return "Cette connexion n'autorise pas encore la lecture des produits. Reconnectez la boutique et acceptez l'autorisation Produits.";
  }

  if (code === "remote_store_unavailable") {
    return "La boutique distante ne repond pas pour le moment. Reessayez la synchronisation dans quelques minutes.";
  }

  if (status >= 500) {
    return "Le service est temporairement indisponible. Nous gardons vos donnees intactes, reessayez dans quelques minutes.";
  }

  if (
    code === "store_already_connected_with_other_teqa_store"
    || normalized.includes("another teqa store")
    || normalized.includes("autre compte")
  ) {
    return "Cette boutique est deja connectee a un autre compte Teqa. Verifiez que vous utilisez le bon compte store, ou contactez le support pour transferer la connexion.";
  }

  if (
    code === "store_already_connected"
    || normalized.includes("already connected")
    || normalized.includes("deja connect")
    || normalized.includes("déjà connect")
  ) {
    return "Cette boutique est deja connectee a votre compte Teqa. Retrouvez-la dans la liste de vos integrations.";
  }

  if (code === "assignment_conflict" || normalized.includes("conflit") || normalized.includes("conflict")) {
    return "Cette affectation entre en conflit avec une collaboration active. Une source complete bloque tous ses produits, et un produit deja affecte ne peut pas etre affecte une deuxieme fois.";
  }

  if (status === 403) {
    return "Vous n'avez pas l'autorisation pour cette action avec ce role. Verifiez vos permissions ou utilisez le compte proprietaire.";
  }

  if (status === 401) {
    return "Votre session a expire. Reconnectez-vous puis relancez l'action.";
  }

  return raw || fallback;
}

export function FeedbackMessage({ type = "info", title, message, onClose }) {
  const Icon = ICONS[type] || Info;
  return (
    <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 shadow-sm ${STYLES[type] || STYLES.info}`}>
      <Icon size={18} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        {title ? <p className="text-sm font-semibold">{title}</p> : null}
        {message ? <p className="text-sm leading-5">{message}</p> : null}
      </div>
      {onClose ? (
        <button type="button" onClick={onClose} className="shrink-0 opacity-80 hover:opacity-100" aria-label="Close">
          <XCircle size={15} />
        </button>
      ) : null}
    </div>
  );
}

export function LoadingState({ label = "Chargement..." }) {
  return (
    <div className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white p-8 text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
      <Loader2 size={26} className="animate-spin text-indigo-500" />
      <p className="text-sm font-medium">{label}</p>
    </div>
  );
}

export function EmptyState({ title = "Aucune donnee pour le moment", message = "Quand les premieres donnees seront disponibles, elles apparaitront ici.", action }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-900/60">
      <Info size={24} className="mx-auto mb-3 text-slate-400" />
      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">{message}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}
